import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { ChatMarkdown } from '@/modules/common/Chat/components/ChatMarkdown';
import { getInteractiveMessageInputSx } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.styles';
import { getInteractiveMessageResponse } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import {
  InteractiveMessageActionConfig,
  InteractiveMessageCard,
} from '@/modules/household/chat/components/messages/interactive/InteractiveMessageCard';
import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import { visuallyHidden } from '@/styles/a11y';
import { COLORS } from '@/styles/tokens/colors';
import { MessageResponseAction, MessageStatus } from '@/types/schema';

import {
  buildMultipleChoiceSubmitInput,
  canSubmitMultipleChoiceSelection,
  EMPTY_MULTIPLE_CHOICE_SELECTION,
  MultipleChoiceVariant,
  toggleOptionValue,
  toggleOtherOption,
} from './multipleChoiceQuestionMessage.utils';

interface MultipleChoiceQuestionMessageRendererProps {
  message: HouseholdChatMessage;
  variant: MultipleChoiceVariant;
}

const MULTIPLE_CHOICE_ACTIONS: Record<
  MessageResponseAction,
  InteractiveMessageActionConfig
> = {
  [MessageResponseAction.Dismiss]: { label: 'Cancel', variant: 'secondary' },
  [MessageResponseAction.Submit]: { label: 'Submit', variant: 'primary' },
};

const VALIDATION_MULTIPLE_CHOICE_ACTIONS = {
  ...MULTIPLE_CHOICE_ACTIONS,
  [MessageResponseAction.Dismiss]: { label: 'Skip', variant: 'secondary' },
} satisfies Record<MessageResponseAction, InteractiveMessageActionConfig>;

const MULTIPLE_CHOICE_RESPONDED_VERBS: Record<MessageResponseAction, string> = {
  [MessageResponseAction.Submit]: 'answered',
  [MessageResponseAction.Dismiss]: 'skipped this question',
};

/** Shared row treatment: compact clickable row, gray when selected. */
const getOptionRowSx = ({
  isDisabled,
  isSelected,
  showFocusOutline = true,
}: {
  isDisabled: boolean;
  isSelected: boolean;
  showFocusOutline?: boolean;
}) => {
  const hoverBorderColor = isSelected ? COLORS.GRAY[300] : COLORS.GRAY[200];
  const outlineSx = {
    outline: `1px solid ${hoverBorderColor}`,
    outlineOffset: '-1px',
  };

  return {
    m: 0,
    p: 1,
    gap: 1,
    width: '100%',
    borderRadius: '4px',
    bgcolor: isSelected ? COLORS.GRAY[100] : undefined,
    cursor: isDisabled ? 'default' : 'pointer',
    opacity: isDisabled ? 0.6 : 1,
    '&:hover': isDisabled ? undefined : outlineSx,
    '&:focus-within': isDisabled || !showFocusOutline ? undefined : outlineSx,
  };
};

interface SelectionControlProps {
  variant: MultipleChoiceVariant;
  id: string;
  /**
   * Input name: the shared per-question group name for radios (so they form
   * a native radio group with roving focus), or a unique name per checkbox.
   */
  name: string;
  /**
   * Optional explicit accessible name for the control.
   */
  ariaLabel?: string;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

/** The row's checkbox (multi-select) or radio button (single-select). */
function SelectionControl({
  variant,
  id,
  name,
  ariaLabel,
  isSelected,
  isDisabled,
  onToggle,
}: SelectionControlProps) {
  return (
    <>
      <Box
        component="input"
        type={variant === 'singleSelect' ? 'radio' : 'checkbox'}
        data-testid={variant === 'singleSelect' ? id : `checkbox-${name}`}
        aria-label={ariaLabel}
        sx={{
          ...visuallyHidden,
          '&:focus-visible + span': {
            outline: `2px solid ${COLORS.BLUE[600]}`,
            outlineOffset: '2px',
          },
        }}
        id={id}
        name={name}
        checked={isSelected}
        disabled={isDisabled}
        onChange={onToggle}
      />
      <Box
        component="span"
        aria-hidden
        sx={{
          alignItems: 'center',
          bgcolor: isSelected ? COLORS.GRAY[700] : COLORS.PRIMITIVES.WHITE,
          border: isSelected ? 'none' : `1px solid ${COLORS.GRAY[200]}`,
          borderRadius: variant === 'singleSelect' ? '50%' : '4px',
          boxSizing: 'border-box',
          display: 'inline-flex',
          flexShrink: 0,
          height: 16,
          justifyContent: 'center',
          width: 16,
        }}
      >
        {isSelected && variant === 'multiSelect' && (
          <CheckIcon
            size={12}
            color={COLORS.PRIMITIVES.WHITE}
            strokeWidth={2}
          />
        )}
        {isSelected && variant === 'singleSelect' && (
          <Box
            component="span"
            sx={{
              bgcolor: COLORS.PRIMITIVES.WHITE,
              borderRadius: '50%',
              height: 6,
              width: 6,
            }}
          />
        )}
      </Box>
    </>
  );
}

interface OtherOptionRowProps {
  variant: MultipleChoiceVariant;
  controlId: string;
  controlName: string;
  isSelected: boolean;
  isDisabled: boolean;
  otherText: string;
  onToggle: () => void;
  onOtherTextChange: (text: string) => void;
}

/**
 * The "Other" escape-hatch row. The control and label stay mounted across
 * selection changes, and the selected state reveals a full-width text field
 * below them. The field sits outside the label so typing does not toggle the
 * selection.
 */
function OtherOptionRow({
  variant,
  controlId,
  controlName,
  isSelected,
  isDisabled,
  otherText,
  onToggle,
  onOtherTextChange,
}: OtherOptionRowProps) {
  let optionLabel = 'Other';
  if (!isSelected && variant === 'multiSelect') {
    optionLabel = 'Other...';
  }

  return (
    <Stack
      sx={getOptionRowSx({
        isDisabled,
        isSelected,
        showFocusOutline: false,
      })}
    >
      <Stack
        component="label"
        htmlFor={controlId}
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ cursor: isDisabled ? 'default' : 'pointer', width: '100%' }}
      >
        <SelectionControl
          variant={variant}
          id={controlId}
          name={controlName}
          ariaLabel="Other"
          isSelected={isSelected}
          isDisabled={isDisabled}
          onToggle={onToggle}
        />
        <Typography
          component="span"
          variant="body-xs"
          sx={{ color: COLORS.TEXT.PRIMARY, flexGrow: 1 }}
        >
          {optionLabel}
        </Typography>
      </Stack>
      {isSelected ? (
        <Box sx={{ width: '100%' }}>
          <TextInput
            id={`${controlId}-text`}
            label="Other response"
            hideLabel
            placeholder="Type your response..."
            autoFocus
            fullWidth
            value={otherText}
            disabled={isDisabled}
            onChange={(event) => onOtherTextChange(event.target.value)}
            sx={getInteractiveMessageInputSx(COLORS.GRAY[300])}
          />
        </Box>
      ) : null}
    </Stack>
  );
}

/**
 * Renders a multiple-choice question (single- or multi-select per `variant`)
 * as an interactive message card. Questions offer an "Other" free-form answer
 * unless their content explicitly hides it.
 */
export function MultipleChoiceQuestionMessageRenderer({
  message,
  variant,
}: MultipleChoiceQuestionMessageRendererProps) {
  const [selection, setSelection] = useState(EMPTY_MULTIPLE_CHOICE_SELECTION);
  const [otherText, setOtherText] = useState('');
  const {
    isValidationQuestionsConversation,
    respondToMessage,
    respondingToMessage,
  } = useHouseholdChatContext();

  const questionContent =
    variant === 'singleSelect'
      ? message.multipleChoiceSingleSelectContent
      : message.multipleChoiceMultiSelectContent;
  const response = getInteractiveMessageResponse(message);
  const isSubmitting = respondingToMessage?.messageId === message.id;

  if (!questionContent) {
    // The typed question is decrypted server-side. If it cannot be resolved,
    // preserve the persisted plain-text question instead of rendering an
    // empty card.
    return <ChatMarkdown markdown={message.content} />;
  }

  const handleRespond = (action: MessageResponseAction) => {
    if (action === MessageResponseAction.Submit) {
      void respondToMessage(
        buildMultipleChoiceSubmitInput(message.id, selection, otherText)
      );
      return;
    }
    void respondToMessage({ messageID: message.id, responseAction: action });
  };

  // The shared radio-group name; also prefixes each control's id, with
  // distinct `option`/`other` segments so a declared option whose value is
  // literally "other" can't collide with the "Other" row's ids.
  const controlNamePrefix = `multiple-choice-${message.id}`;
  const otherControlId = `${controlNamePrefix}-other`;

  return (
    <InteractiveMessageCard
      message={message}
      title={questionContent.title}
      actions={
        isValidationQuestionsConversation
          ? VALIDATION_MULTIPLE_CHOICE_ACTIONS
          : MULTIPLE_CHOICE_ACTIONS
      }
      disabledActions={{
        [MessageResponseAction.Submit]: !canSubmitMultipleChoiceSelection(
          selection,
          otherText
        ),
      }}
      onRespond={handleRespond}
      respondedVerbs={MULTIPLE_CHOICE_RESPONDED_VERBS}
    >
      {message.status === MessageStatus.Pending ? (
        <Stack
          gap={0.25}
          role={variant === 'singleSelect' ? 'radiogroup' : 'group'}
          aria-label={questionContent.title}
        >
          {questionContent.options.map((option) => {
            const isSelected = selection.selectedValues.includes(option.value);
            const optionControlId = `${controlNamePrefix}-option-${option.value}`;
            return (
              <Stack
                key={option.value}
                component="label"
                htmlFor={optionControlId}
                direction="row"
                alignItems="flex-start"
                sx={getOptionRowSx({
                  isDisabled: isSubmitting,
                  isSelected,
                })}
              >
                <SelectionControl
                  variant={variant}
                  id={optionControlId}
                  name={
                    variant === 'singleSelect'
                      ? controlNamePrefix
                      : optionControlId
                  }
                  isSelected={isSelected}
                  isDisabled={isSubmitting}
                  onToggle={() =>
                    setSelection(
                      toggleOptionValue(selection, variant, option.value)
                    )
                  }
                />
                <Typography
                  component="span"
                  variant="body-xs"
                  sx={{ color: COLORS.TEXT.PRIMARY, flex: 1 }}
                >
                  {option.label}
                </Typography>
              </Stack>
            );
          })}
          {!questionContent.hideOtherOption && (
            <OtherOptionRow
              variant={variant}
              controlId={otherControlId}
              controlName={
                variant === 'singleSelect' ? controlNamePrefix : otherControlId
              }
              isSelected={selection.isOtherSelected}
              isDisabled={isSubmitting}
              otherText={otherText}
              onToggle={() =>
                setSelection(toggleOtherOption(selection, variant))
              }
              onOtherTextChange={setOtherText}
            />
          )}
        </Stack>
      ) : (
        response?.responseAction === MessageResponseAction.Submit && (
          <Typography
            variant="title-xs"
            sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
          >
            {response.content}
          </Typography>
        )
      )}
    </InteractiveMessageCard>
  );
}

/** Registry adapter: MULTIPLE_CHOICE_SINGLE_SELECT_MESSAGE rows. */
export function MultipleChoiceSingleSelectMessageRenderer({
  message,
}: {
  message: HouseholdChatMessage;
}) {
  return (
    <MultipleChoiceQuestionMessageRenderer
      message={message}
      variant="singleSelect"
    />
  );
}

/** Registry adapter: MULTIPLE_CHOICE_MULTI_SELECT_MESSAGE rows. */
export function MultipleChoiceMultiSelectMessageRenderer({
  message,
}: {
  message: HouseholdChatMessage;
}) {
  return (
    <MultipleChoiceQuestionMessageRenderer
      message={message}
      variant="multiSelect"
    />
  );
}
